import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Utensils, CalendarDays, Bot, BookOpen, Info } from 'lucide-react';
import { useSEO, SITE_URL } from '@/lib/seo';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter, CONTACT_EMAIL } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { POSTS } from '@/lib/blogPosts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes`);
        if (cancelled) return;
        const regions = [...new Set(data.map((r) => r.region).filter(Boolean))];
        setCounts({ total: data.length, regions });
      } catch (e) {
        /* counts are optional — the page reads fine without them */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useSEO({
    title: 'About Masala Spin — What This Indian Recipe Site Is | Masala Spin',
    description:
      'What Masala Spin is, how the recipe wheel works, which cuisines it covers, and how recipe times and calorie estimates are handled.',
    canonical: `${SITE_URL}/about`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Masala Spin',
      url: `${SITE_URL}/about`,
      publisher: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
    },
  });

  const features = [
    {
      icon: Utensils,
      title: 'The recipe wheel',
      body: 'Pick a meal type — breakfast, lunch, dinner, drinks or desserts — apply a dietary filter if you want one, then spin. The wheel picks at random from the recipes that match, and opens the full recipe. You can also build a custom wheel from a shortlist of dishes you actually feel like eating.',
    },
    {
      icon: CalendarDays,
      title: 'Weekly meal planner',
      body: 'Assign breakfast, lunch and dinner to each day of the week. The plan is saved in your own browser, and you can email a copy to yourself or share it on WhatsApp.',
    },
    {
      icon: Bot,
      title: 'AI chef suggestions',
      body: 'Type in the ingredients you have and an AI model suggests dishes you could make with them. These suggestions are generated by a language model, not written by us, so treat them as ideas to sanity-check rather than tested recipes.',
    },
    {
      icon: BookOpen,
      title: 'Recipe blog',
      body: `Long-form guides that group recipes by occasion, region or grain — currently ${POSTS.length} articles covering Maharashtrian dinners, Kerala breakfasts, millet cooking, a 21-day meal plan and 50 weeknight dinner ideas. Every dish mentioned links to its recipe page.`,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <Breadcrumbs items={[{ name: 'About' }]} />

        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-6">
          About Masala Spin
        </h1>

        <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed space-y-5">
          <p className="text-lg">
            Masala Spin is a free Indian recipe site built around a single, very ordinary problem:
            deciding what to cook. Instead of a search box that assumes you already know what you
            want, it gives you a wheel to spin and a collection of recipes to browse.
          </p>
          <p>
            The site is independently run and has no company, restaurant or brand behind it. It is
            funded by advertising, which is why you will see ad slots on some pages.
          </p>
        </div>

        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mt-12 mb-6">
          What you can do here
        </h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="p-5 rounded-2xl bg-white border border-[#1C1917]/5">
                <Icon className="w-5 h-5 text-[#F97316] mb-3" aria-hidden="true" />
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1C1917] mb-2">
                  {f.title}
                </h3>
                <p className="text-sm text-[#1C1917]/70 font-['Outfit'] leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>

        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mt-12 mb-4">
          The recipes we cover
        </h2>
        <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed space-y-4">
          <p>
            The focus is Indian home cooking: everyday breakfasts, dal-rice-sabzi lunches,
            weeknight dinners, drinks and desserts, plus a set of Indo-Western fusion dishes.
            {counts
              ? ` There are currently ${counts.total} recipes on the site, including regional
                 collections for ${counts.regions.join(', ')}.`
              : ' Regional collections currently cover Maharashtra, Goa and Kerala.'}
          </p>
          <p>
            Regional dishes are tagged with their region so you can browse a single cuisine, and the
            <Link to="/blog" className="text-[#F97316] hover:underline"> recipe blog </Link>
            groups them into guides — for example
            <Link to="/blog/kerala-breakfast-recipes-8-authentic-dishes" className="text-[#F97316] hover:underline"> eight Kerala breakfasts </Link>
            or
            <Link to="/blog/10-easy-maharashtrian-dinner-recipes-for-beginners" className="text-[#F97316] hover:underline"> ten beginner-friendly Maharashtrian dinners</Link>.
          </p>
        </div>

        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mt-12 mb-4">
          How recipe and nutrition information is handled
        </h2>
        <div
          className="p-5 rounded-2xl border border-[#F97316]/25 bg-[#F97316]/5 font-['Outfit'] text-sm text-[#1C1917]/80 leading-relaxed space-y-3"
          data-testid="about-transparency-note"
        >
          <p className="flex items-start gap-2">
            <Info className="w-4 h-4 text-[#F97316] mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              We would rather be transparent about the limits of our data than pretend it is more
              precise than it is:
            </span>
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Times are approximate.</strong> Each recipe card shows a single overall time.
              It is a guide, not a tested kitchen measurement, and it will vary with your stove,
              cookware and how fast you chop.
            </li>
            <li>
              <strong>Calorie figures are estimates.</strong> They are rough per-serving estimates
              for the dish as described. They are not lab-tested values, and they change with your
              portion size and how much fat you cook with.
            </li>
            <li>
              <strong>Ingredient lists do not always carry measured quantities.</strong> Many of our
              recipes list what goes in rather than exact grams, in the way most Indian home cooking
              is passed on. Season and scale to taste.
            </li>
            <li>
              <strong>Nothing here is medical or dietary advice.</strong> Dietary tags such as
              "vegan", "gluten-free" or "diabetic-friendly" describe the dish in general terms.
              Always check labels and speak to a qualified professional about your own diet.
            </li>
            <li>
              <strong>AI-generated suggestions are labelled as such.</strong> The chef bot's ideas
              are produced by a language model and are not reviewed dish by dish.
            </li>
          </ul>
          <p>
            Spotted something wrong — a mistaken ingredient, an impossible time, a calorie figure
            that looks off? Please tell us and we will fix it.
          </p>
        </div>

        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mt-12 mb-4">
          Contact
        </h2>
        <p className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed">
          You can reach us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
            {CONTACT_EMAIL}
          </a>{' '}
          or through the{' '}
          <Link to="/contact" className="text-[#F97316] hover:underline" data-testid="about-contact-link">
            contact page
          </Link>
          . Recipe corrections, nutrition corrections, copyright concerns, recipe submissions and
          advertising enquiries are all welcome.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
